import type { ProgramLinkSource } from "@/functions-shared/offer/sessionLinkDefaults";
import { prefillSessionLinks, type PrefilledSessionLink } from "./sessionLinkPrefill";

export type ProgramDefaultField = "description" | "eligibilityNotes" | PrefilledSessionLink;

export interface ProgramDefaultsSource extends ProgramLinkSource {
  description?: string | null;
  eligibilityNotes?: string | null;
  defaultSessionDescription?: string | null;
}

/**
 * KB-4351 — what choosing a program writes into a session form (bulk add sessions, the session dialog):
 * description, eligibility notes, and the two links through `prefillSessionLinks`. The JSX handlers only
 * call this, so no handler maps a program field onto an input itself, and the mapping is tested here.
 *
 * `shouldWrite` is the site's own gate (bulk add: the field is not locked; the dialog: always).
 * `legacyDescriptionFallback` keeps the session dialog's existing fallback to `defaultSessionDescription`.
 */
export function applyProgramToSessionForm(
  program: ProgramDefaultsSource,
  setValue: (field: ProgramDefaultField, value: string) => void,
  shouldWrite: (field: ProgramDefaultField) => boolean = () => true,
  options: { legacyDescriptionFallback?: boolean } = {},
): void {
  const description = options.legacyDescriptionFallback
    ? program.description || program.defaultSessionDescription || ""
    : program.description || "";
  if (shouldWrite("description")) setValue("description", description);
  if (shouldWrite("eligibilityNotes")) setValue("eligibilityNotes", program.eligibilityNotes || "");
  prefillSessionLinks(program, (field, value) => setValue(field, value), (field) => shouldWrite(field));
}
